import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';

import { cn } from '../lib/cn';
import { FIELD_BORDER, FIELD_FOCUS } from '../lib/recipes';

import { useFieldControl } from './field';

/**
 * `.form-input` verbatim (tokens doc §7.3): 11px 14px padding, 10px radius, the 1.5px
 * border, 15px ink text, and a blue border plus the soft 12%-alpha ring on focus.
 */
export const FIELD_SHELL = cn(
  'w-full rounded-input bg-surface px-[14px] py-[11px] text-md text-ink',
  FIELD_BORDER,
  FIELD_FOCUS,
  'transition-[border-color,box-shadow] duration-200',
  'placeholder:text-neutral-400-text',
  'disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-400-text',
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:border-danger',
);

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

/**
 * Text input. Inside a <Field> it picks up its id and `aria-describedby` automatically;
 * standalone it needs an `id` and an external label like any native input.
 */
export function Input({ className, id, ...rest }: InputProps) {
  const field = useFieldControl();
  return (
    <input
      id={id ?? field?.controlId}
      aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
      aria-invalid={rest['aria-invalid'] ?? (field?.invalid === true ? true : undefined)}
      required={rest.required ?? field?.required}
      className={cn(FIELD_SHELL, className)}
      {...rest}
    />
  );
}

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

/** Same shell as Input, with a sane default height and vertical-only resizing. */
export function Textarea({ className, id, rows = 4, ...rest }: TextareaProps) {
  const field = useFieldControl();
  return (
    <textarea
      id={id ?? field?.controlId}
      rows={rows}
      aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
      aria-invalid={rest['aria-invalid'] ?? (field?.invalid === true ? true : undefined)}
      required={rest.required ?? field?.required}
      className={cn(FIELD_SHELL, 'resize-y leading-[22px]', className)}
      {...rest}
    />
  );
}
